import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getBoletaEstudiante } from '../../../api/calificaciones';
import styles from '../../../styles/EstudiantesStyles/Estudiantes.module.css';
import boletaStyles from '../../../styles/EstudiantesStyles/Boleta.module.css';
import { generateBoletaCalificacionesPDF } from '../../../utils/generateBoletaCalificacionesPDF';



// Calcula la calificación final según reglas de redondeo especial
const calcularFinal = (p1, p2, p3) => {
  if (p1 == null || p2 == null || p3 == null) return '';
  const total = (Number(p1) + Number(p2) + Number(p3)) / 3;
  if (total === 0) return 0;
  if (total < 6) return 5;
  if (total < 7) return 6;
  if (total < 7.5) return 7;
  if (total < 8.5) return 8;
  if (total < 9.5) return 9;
  return 10;
};

const scoreMateria = (m) => {
  const estado = String(m?.estado || '').toUpperCase();
  const vals = [m?.parcial_1, m?.parcial_2, m?.parcial_3, m?.final, m?.extraordinario];
  const totalCalificaciones = vals.filter(v => v !== null && v !== undefined).length;

  return [
    estado !== 'RETIRADO' ? 1 : 0,
    totalCalificaciones,
    m?.final !== null && m?.final !== undefined ? 1 : 0,
    Number(m?.id_estudiante_grupo || 0)
  ];
};

const isBetterMateria = (next, current) => {
  if (!current) return true;
  const a = scoreMateria(next);
  const b = scoreMateria(current);
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] > b[i]) return true;
    if (a[i] < b[i]) return false;
  }
  return false;
};

const normalizarBoleta = (data) => {
  if (!data || !Array.isArray(data.ciclos)) return data;

  const ciclos = data.ciclos.map((ciclo) => {
    if (!Array.isArray(ciclo.materias)) return ciclo;

    const byMateria = new Map();
    ciclo.materias.forEach((m) => {
      const key = m?.id_materia != null ? `m-${m.id_materia}` : `g-${m?.id_grupo ?? m?.id_estudiante_grupo}`;
      const current = byMateria.get(key);
      if (isBetterMateria(m, current)) {
        byMateria.set(key, m);
      }
    });

    const materias = Array.from(byMateria.values()).sort((a, b) => {
      const na = String(a?.materia_nombre || '').toLowerCase();
      const nb = String(b?.materia_nombre || '').toLowerCase();
      if (na < nb) return -1;
      if (na > nb) return 1;
      return String(a?.grupo_codigo || '').localeCompare(String(b?.grupo_codigo || ''));
    });

    return { ...ciclo, materias };
  });

  return { ...data, ciclos };
};
  
function Calificaciones({ student }) {
  const [boleta, setBoleta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCicloId, setSelectedCicloId] = useState('');

  const estudianteId = student?.id_estudiante || student?.id;

  const cargarBoleta = useCallback(async () => {
    if (!estudianteId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getBoletaEstudiante(estudianteId);
      setBoleta(normalizarBoleta(data));
    } catch (err) {
      console.error('[Calificaciones] Error al cargar boleta:', err);
      setError('No se pudieron cargar las calificaciones. Intente de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [estudianteId]);

  useEffect(() => {
    cargarBoleta();
  }, [cargarBoleta]);

  useEffect(() => {
    const handleGruposUpdated = (event) => {
      const updatedStudentId = event?.detail?.id_estudiante;
      if (updatedStudentId && Number(updatedStudentId) !== Number(estudianteId)) {
        return;
      }
      cargarBoleta();
    };

    window.addEventListener('estudiantes-grupos:updated', handleGruposUpdated);
    return () => {
      window.removeEventListener('estudiantes-grupos:updated', handleGruposUpdated);
    };
  }, [cargarBoleta, estudianteId]);

  const ciclosDisponibles = useMemo(() => boleta?.ciclos || [], [boleta]);

  const ciclosFiltrados = useMemo(() => {
    if (!selectedCicloId) return ciclosDisponibles;
    return ciclosDisponibles.filter(
      (ciclo) => String(ciclo?.id_ciclo_escolar) === String(selectedCicloId)
    );
  }, [ciclosDisponibles, selectedCicloId]);

  useEffect(() => {
    if (!selectedCicloId) return;

    const exists = ciclosDisponibles.some(
      (ciclo) => String(ciclo?.id_ciclo_escolar) === String(selectedCicloId)
    );

    if (!exists) {
      setSelectedCicloId('');
    }
  }, [ciclosDisponibles, selectedCicloId]);

  const totalMateriasFiltradas = useMemo(
    () => ciclosFiltrados.reduce((acc, ciclo) => acc + ((ciclo?.materias || []).length), 0),
    [ciclosFiltrados]
  );

  // Generar PDF de la boleta según filtro de ciclo seleccionado
  const handleGenerarPDF = () => {
    if (!boleta || totalMateriasFiltradas === 0) return;
    generateBoletaCalificacionesPDF({
      encabezado: boleta.encabezado,
      ciclos: ciclosFiltrados,
      descargar: true
    });
  };

  // Helpers
  const formatCal = (val) => {
    if (val === null || val === undefined) return '';
    const n = Number(val);
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
  };

  const getCalClass = (val) => {
    if (val === null || val === undefined) return '';
    return Number(val) >= 7 ? boletaStyles.aprobada : boletaStyles.reprobada;
  };

  const calcPromedio = (materias) => {
    // Promedio de calificación final donde exista
    const conFinal = materias.filter(m => m.final !== null && m.final !== undefined);
    if (conFinal.length === 0) return null;
    const sum = conFinal.reduce((a, m) => a + Number(m.final), 0);
    return (sum / conFinal.length).toFixed(1);
  };

  // Render
  if (loading) {
    return (
      <div className={styles.tabPanel}>
        <div className={boletaStyles.loadingState}>
          <div className={boletaStyles.spinner} />
          <p>Cargando boleta de calificaciones…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.tabPanel}>
        <div className={boletaStyles.errorState}>
          <span className={boletaStyles.errorIcon}>⚠️</span>
          <p>{error}</p>
          <button className={boletaStyles.retryBtn} onClick={cargarBoleta}>Reintentar</button>
        </div>
      </div>
    );
  }

  if (!boleta || !boleta.ciclos || boleta.ciclos.length === 0) {
    return (
      <div className={styles.tabPanel}>
        <div className={boletaStyles.emptyState}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
              stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M14 2V8H20" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <p>No hay calificaciones registradas para este estudiante.</p>
        </div>
      </div>
    );
  }

  const enc = boleta.encabezado;

  return (
    <div className={styles.tabPanel}>
      {/* --------- Encabezado de boleta --------- */}
      <div className={boletaStyles.boletaContainer}>
        <div className={boletaStyles.boletaHeader}>
          <div className={boletaStyles.boletaHeaderLeft}>
            <h2 className={boletaStyles.boletaTitle}>Boleta de Calificaciones</h2>
            <span className={boletaStyles.boletaPrograma}>{enc.programa}</span>
          </div>
          <div className={boletaStyles.boletaHeaderRight}>
            <div className={boletaStyles.cicloFilterWrap}>
              <label htmlFor="boleta-ciclo" className={boletaStyles.cicloFilterLabel}>Ciclo</label>
              <select
                id="boleta-ciclo"
                className={boletaStyles.cicloFilterSelect}
                value={selectedCicloId}
                onChange={(e) => setSelectedCicloId(e.target.value)}
              >
                <option value="">Todos</option>
                {ciclosDisponibles.map((ciclo) => (
                  <option key={ciclo?.id_ciclo_escolar || ciclo?.nombre_ciclo} value={ciclo?.id_ciclo_escolar || ''}>
                    {ciclo?.nombre_ciclo || 'Sin ciclo'}
                  </option>
                ))}
              </select>
            </div>
            <button className={boletaStyles.refreshBtn} onClick={cargarBoleta} title="Actualizar boleta">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M23 4v6h-6"/><path d="M1 20v-6h6"/>
                <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
              </svg>
            </button>
            <button className={boletaStyles.refreshBtn} onClick={handleGenerarPDF} title="Generar PDF" disabled={totalMateriasFiltradas === 0}>
              PDF
            </button>
          </div>
        </div>

        {/* Datos del estudiante */}
        <div className={boletaStyles.boletaInfo}>
          <div className={boletaStyles.infoItem}>
            <span className={boletaStyles.infoLabel}>Matrícula</span>
            <span className={boletaStyles.infoValue}>{enc.matricula}</span>
          </div>
          <div className={boletaStyles.infoItem}>
            <span className={boletaStyles.infoLabel}>Nombre</span>
            <span className={boletaStyles.infoValue}>{enc.nombre_completo}</span>
          </div>
          <div className={boletaStyles.infoItem}>
            <span className={boletaStyles.infoLabel}>CURP</span>
            <span className={boletaStyles.infoValue}>{enc.curp || 'N/A'}</span>
          </div>
          <div className={boletaStyles.infoItem}>
            <span className={boletaStyles.infoLabel}>Campus</span>
            <span className={boletaStyles.infoValue}>{enc.campus}</span>
          </div>
        </div>

        {/* --------- Tabla por ciclo --------- */}
        {ciclosFiltrados.map((ciclo) => {
          const promedio = calcPromedio(ciclo.materias);
          return (
            <div key={ciclo.id_ciclo_escolar} className={boletaStyles.cicloBlock}>
              <div className={boletaStyles.cicloHeader}>
                <span className={boletaStyles.cicloName}>{ciclo.nombre_ciclo}</span>
                {promedio !== null && (
                  <span className={boletaStyles.cicloPromedio}>Promedio: {promedio}</span>
                )}
              </div>

              <div className={boletaStyles.tableWrapper}>
                <table className={boletaStyles.boletaTable}>
                  <thead>
                    <tr>
                      <th className={boletaStyles.thAsignatura}>Asignatura</th>
                      <th className={boletaStyles.thCal}>P1</th>
                      <th className={boletaStyles.thCal}>P2</th>
                      <th className={boletaStyles.thCal}>P3</th>
                      <th className={boletaStyles.thCal}>Final</th>
                      <th className={boletaStyles.thCal}>Extra.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ciclo.materias.map((m) => (
                      <tr key={m.id_estudiante_grupo}>
                        <td className={boletaStyles.tdAsignatura}>
                          <span className={boletaStyles.materiaNombre}>{m.materia_nombre}</span>
                          {m.materia_clave && (
                            <span className={boletaStyles.materiaClave}>{m.materia_clave}</span>
                          )}
                        </td>
                        <td className={`${boletaStyles.tdCal} ${getCalClass(m.parcial_1)}`}>{formatCal(m.parcial_1)}</td>
                        <td className={`${boletaStyles.tdCal} ${getCalClass(m.parcial_2)}`}>{formatCal(m.parcial_2)}</td>
                        <td className={`${boletaStyles.tdCal} ${getCalClass(m.parcial_3)}`}>{formatCal(m.parcial_3)}</td>
                        <td className={`${boletaStyles.tdCal} ${boletaStyles.tdFinal} ${getCalClass(m.final != null ? m.final : calcularFinal(m.parcial_1, m.parcial_2, m.parcial_3))}`}>
                          {formatCal(m.final != null ? m.final : calcularFinal(m.parcial_1, m.parcial_2, m.parcial_3))}
                        </td>
                        <td className={`${boletaStyles.tdCal} ${getCalClass(m.extraordinario)}`}>{formatCal(m.extraordinario)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}

        {/* Pie de boleta */}
        <div className={boletaStyles.boletaFooter}>
          <p className={boletaStyles.footerNote}>
            La calificación mínima aprobatoria es <strong>7 (siete)</strong>.
          </p>
          <p className={boletaStyles.footerNote}>
            Simbología: P1 = Parcial 1, P2 = Parcial 2, P3 = Parcial 3, Extra. = Extraordinario.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Calificaciones;
